import React, { ChangeEvent, FC, useContext, useEffect, useState } from 'react';
import { Autocomplete, Checkbox, FormControlLabel, FormGroup, TextField, Tooltip, Typography } from '@mui/material';
import { Grid } from '@mui/system';
import { FORMS_TYPES } from 'common/types';
import {
  ENTERPRISE_EQUITY_OPTIONS,
  ENTERPRISE_VALUE_OPTION,
  REMOVE_FROM_VALUATION_SUMMARY,
  USE_FORWARD_MULTIPLE_DISCOUNT,
  USE_MULTIPLE_PREMIUM_DISCOUNT,
  VALUATIONS_GPC_COMPS_NEED_REFRESH,
} from 'pages/ValuationsAllocation/common/constants/valuations';
import { SelectFinancialsVersions } from 'pages/ValuationsAllocation/components/ApproachDialog/components/SelectFinancialsVersions';
import { ValuationsFormState } from 'pages/ValuationsAllocation/types';
import { VALUATION_APPROACHES_TEST_IDS } from 'pages/ValuationsAllocation/ValuationApproaches.testIds';
import ValuationContext from 'pages/ValuationsAllocation/ValuationContext';
import { FormState, useFormValidation } from 'services/hooks/useFormValidation';
import { getObjectValue, getStringValue } from 'utilities';
import { StyledCustomTextField, StyledFormControl } from './styles';
import { ApproachFormProps } from './types';

const APPROACH_NAME_ID = 'name';
const APPROACH_NAME_LABEL = 'Approach Name';
const ENTERPRISE_EQUITY_ID = 'enterprise_equity_value';
const ENTERPRISE_EQUITY_LABEL = 'Enterprise Value or Equity Value';

const CALIBRATION_NAME_LABEL = 'Calibration Name';
const RANGE_SENSITIVITY_NAME_LABEL = 'Range Sensitivity Name';

const FORM_CONSTRAINTS = Object.freeze({
  name: {
    presence: {
      allowEmpty: false,
      message: 'is required',
    },
    length: {
      message: 'must be at least 2 characters',
      minimum: 2,
    },
  },
});

const ApproachForm: FC<ApproachFormProps> = props => {
  const { formState, onFormChange, shouldDisplayGPCOptions, isEquityOptionAvailable } = props;

  const [formValues, setFormValues] = useState<ValuationsFormState['values']>(formState.values);

  const {
    disableFinancialsSelection,
    isEditingApproachName,
    isChangingFinancialStatement,
    isEditingCalibrationName,
    isEditingRangeSensitivityName,
  } = useContext(ValuationContext);

  const { formValidated, validateForm } = useFormValidation(FORM_CONSTRAINTS);
  const formValidatedData = getObjectValue(formValidated);
  const errors = formValidatedData?.errors as FORMS_TYPES.FormFieldErrors;

  // Validate Form
  useEffect(() => {
    validateForm(formValues as FormState['values']);
  }, [formValues, validateForm]);

  // Update Form
  useEffect(() => {
    onFormChange?.(formValidated);
  }, [formValidated, onFormChange]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const {
      target: { name, value },
    } = event;

    setFormValues(prevState => ({
      ...prevState,
      [name]: value,
      use_forward_multiple_discount: name === ENTERPRISE_EQUITY_ID && value === ENTERPRISE_VALUE_OPTION,
    }));
  };

  return (
    <Grid container>
      {isEditingApproachName && (
        <>
          <StyledCustomTextField
            variant="standard"
            dbErrors={null}
            formErrors={errors}
            fullWidth
            id={APPROACH_NAME_ID}
            inputProps={{
              'data-testid': VALUATION_APPROACHES_TEST_IDS.approachDialogNameInput,
            }}
            label={APPROACH_NAME_LABEL}
            name={APPROACH_NAME_ID}
            onBlur={null}
            onChange={handleChange}
            onFocus={null}
            required
            type="text"
            value={getStringValue(formValues?.name)}
          />
          {shouldDisplayGPCOptions && (
            <FormGroup row style={{ fontSize: '0.5rem', width: '100%' }}>
              <StyledFormControl>
                <Tooltip title={!isEquityOptionAvailable ? VALUATIONS_GPC_COMPS_NEED_REFRESH : ''}>
                  <span>
                    <Autocomplete
                      fullWidth
                      id="enterprise-equity-selector"
                      options={ENTERPRISE_EQUITY_OPTIONS}
                      autoHighlight
                      getOptionLabel={(option: string) => option}
                      value={formValues?.enterprise_equity_value}
                      onChange={(event, value) => {
                        handleChange({
                          target: {
                            name: ENTERPRISE_EQUITY_ID,
                            value,
                          },
                        } as ChangeEvent<HTMLInputElement>);
                      }}
                      sx={{
                        color: theme => theme.palette.grey[500],
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          fullWidth
                          label={ENTERPRISE_EQUITY_LABEL}
                          disabled={!isEquityOptionAvailable}
                          variant="standard"
                        />
                      )}
                      disabled={!isEquityOptionAvailable}
                    />
                  </span>
                </Tooltip>
              </StyledFormControl>
            </FormGroup>
          )}
          <FormGroup row style={{ fontSize: '0.5rem', marginTop: '1.25rem' }}>
            <FormControlLabel
              control={
                <Checkbox
                  name="set-as-calibration"
                  color="primary"
                  checked={formValues?.is_calibration || false}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    event.persist();
                    setFormValues(prevState => ({ ...prevState, is_calibration: event.target.checked }));
                  }}
                />
              }
              label={<Typography variant="body2">{REMOVE_FROM_VALUATION_SUMMARY}</Typography>}
            />
          </FormGroup>
          {shouldDisplayGPCOptions && (
            <FormGroup row style={{ fontSize: '0.5rem', width: '100%' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="set-multiple-premium-discount"
                    color="primary"
                    checked={formValues?.use_multiple_premium_discount || false}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      event.persist();
                      setFormValues(prevState => ({
                        ...prevState,
                        use_multiple_premium_discount: event.target.checked,
                      }));
                    }}
                  />
                }
                label={<Typography variant="body2">{USE_MULTIPLE_PREMIUM_DISCOUNT}</Typography>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="set-forward-multiple-discount"
                    color="primary"
                    checked={formValues?.use_forward_multiple_discount || false}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      event.persist();
                      setFormValues(prevState => ({
                        ...prevState,
                        use_forward_multiple_discount: event.target.checked,
                      }));
                    }}
                  />
                }
                label={<Typography variant="body2">{USE_FORWARD_MULTIPLE_DISCOUNT}</Typography>}
              />
            </FormGroup>
          )}
        </>
      )}
      {isEditingCalibrationName && (
        <StyledCustomTextField
          dbErrors={null}
          formErrors={errors}
          fullWidth
          id={APPROACH_NAME_ID}
          inputProps={{
            'data-testid': VALUATION_APPROACHES_TEST_IDS.approachDialogNameInput,
          }}
          label={CALIBRATION_NAME_LABEL}
          name={APPROACH_NAME_ID}
          onBlur={null}
          onChange={handleChange}
          onFocus={null}
          required
          type="text"
          value={getStringValue(formValues?.name)}
        />
      )}
      {isEditingRangeSensitivityName && (
        <StyledCustomTextField
          dbErrors={null}
          formErrors={errors}
          fullWidth
          id={APPROACH_NAME_ID}
          inputProps={{
            'data-testid': VALUATION_APPROACHES_TEST_IDS.approachDialogNameInput,
          }}
          label={RANGE_SENSITIVITY_NAME_LABEL}
          name={APPROACH_NAME_ID}
          onBlur={null}
          onChange={handleChange}
          onFocus={null}
          required
          type="text"
          value={getStringValue(formValues?.name)}
        />
      )}

      {!disableFinancialsSelection && isChangingFinancialStatement && (
        <SelectFinancialsVersions onChange={handleChange} />
      )}
    </Grid>
  );
};

export default ApproachForm;
